import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NgIf, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { ShiftClosureService } from '../../../core/services/shift-closure.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { firstValueFrom, timer } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { Constants } from '../../utils/constants';
import { isThisMonth } from 'date-fns';
import { FormsModule } from '@angular/forms';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../../../environments/environment';
import { truncateSync } from 'fs';
import { UpdateRecordService } from '../../../core/services/update-record.service';
import { ShiftClosure } from '../../../core/models/shift-closure.model';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [NgIf, RouterModule, FormsModule],
})
export class SidebarComponent implements OnInit {
  expandedSection: string | null = null;
  showConfirmPopup = false;
  modalState = false;
  modalInfo = false;
  shiftInfo = {
    jefeTurno: '',
    fecha: '',
    hora: ''
  };

  currentUser: any = {};
  isDailyShiftOpen: boolean = true //lo puedo ver solo si tengo un turno abierto
  isConflictive: boolean = false

  constructor(
    private authService: AuthService,
    private shiftClosureService: ShiftClosureService,
    private updateRecordService: UpdateRecordService,
    private snackBar: MatSnackBar,
    private router: Router,
    private oidc: OidcSecurityService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }


  async ngOnInit(): Promise<void> {
    //this.isDailyShiftOpen = await this.verifyDailyShift()
    //console.log('INFO: isDailyShiftOpen', this.isDailyShiftOpen)
  }

  private async getOpenShiftClosureInAreaByUser(): Promise<ShiftClosure | null> {
    try {
      const response = await firstValueFrom(this.updateRecordService.getOpenShiftClosureInAreaByUser())

      if (response && typeof response === 'object' && 'id' in response) {
        return response
      } else {
        return null
      }
    } catch (err) {
      return null
    }
  }

  private async closeShiftClosureById(shiftClosureId: number): Promise<ShiftClosure | null> {
    try {
      const response = await firstValueFrom(this.shiftClosureService.closeShiftClosure(shiftClosureId))

      if (response && typeof response === 'object' && 'id' in response) {
        return response as ShiftClosure
      }

      return null
    } catch (err) {
      console.error('Error al cerrar el turno:', err)
      return null
    }
  }

  //buscar un turno abierto para este usuario (fix)
  private async verifyDailyShift(): Promise<boolean> {
    try {
      const shiftId: number = await firstValueFrom(this.shiftClosureService.getOpenByTypeForCurrentUser(Constants.TURNO_DIARIO))
      return shiftId != null && shiftId > 0
    } catch (err) {
      // 404 significa que no hay turno abierto
      return false;
    }
  }

  private async getOpenDailyShift(): Promise<number | null> {
    try {
      const shiftId: number = await firstValueFrom(this.shiftClosureService.getOpenDailyShift())
      return shiftId ? shiftId : null
    } catch (error: any) {
      if (error?.status === 404) {
        return null;
      } else {
        //redirigir a vista enterior
        return null;
      }
    }
  }

  // Función para el modal de Iniciar turno

  async openModal() {
    const userData = localStorage.getItem('currentUser');
    if (userData) {
      this.currentUser = JSON.parse(userData);
    }

    // Obtener fecha y hora actual en Chile
    const chileTimeZone = 'America/Santiago';
    const now = new Date();
    const dateFormatter = new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: chileTimeZone
    });

    const timeFormatter = new Intl.DateTimeFormat('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: chileTimeZone
    });

    this.modalInfo = false;

    this.shiftInfo = {
      jefeTurno: this.currentUser?.username || 'Desconocido',
      fecha: dateFormatter.format(now),
      hora: timeFormatter.format(now),
    };
    this.modalState = true;

    this.isConflictive = await this.existShiftClosureClonfict()

  }

  private async existShiftClosureClonfict(): Promise<boolean> {
    try {
      const shifClosures = await firstValueFrom(this.shiftClosureService.getShiftClosuresByUser())
      const isConflictive: boolean = shifClosures.some(item => item.isReportAvailable == false && item.endUtc != null)
      return isConflictive
    } catch {
      return false
    }
  }

  async iniciarSesion() {
    //verificar si turno esta abierto
    const userId = this.authService.getUserId();
    const openShiftClosure: any = await this.getOpenShiftClosureInAreaByUser()
    console.log('iniciarSesion, openShiftClosure', openShiftClosure)

    if (openShiftClosure) {
      if (openShiftClosure.createdUserId != userId){
        await this.closeShiftClosureById(openShiftClosure.id)
      }
    }
    //cerrar en caso de haber uno abierto
    const hora = this.shiftInfo.hora;
    this.startShiftWithPrecheck(hora);

  }

  closeModal() {
    this.modalState = false;
  }


  toggleSection(section: string) {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  isExpanded(section: string): boolean {
    return this.expandedSection === section;
  }

  hasRole(role: string): boolean {
    return this.authService.hasRole(role);
  }

  /**
   * Verifica si el usuario puede acceder a una funcionalidad específica
   */
  canAccess(feature: string): boolean {
    return this.authService.canAccess(feature);
  }

  /**
   * Devuelve la etiqueta del menú para el Informe de turno según el perfil:
   * - DCS o Novedades (acceso completo): "Informe Jefe de Turno"
   * - Otros perfiles operadores: "Informe Operador [Área]"
   */
  getShiftReportMenuLabel(): string {
    const isFull = this.authService.hasRole('novedades');

    if (isFull) {
      return 'Informe Jefe de Turno';
    }

    // Determinar área por rol operador
    const areaMap: { [key: string]: string } = {
      'Novedades_Operador_Ferrico': 'Cloruro férrico',
      'Novedades_Salmuera_I': 'Salmuera I',
      'Novedades_Salmuera_II': 'Salmuera II',
      'Novedades_Compresion': 'Compresión',
      'Novedades_Electrolizadores': 'Electrolizadores',
      'Novedades_Subestacion': 'Subestación',
      'Novedades_Movimiento_Producto': 'Movimiento de productos',
      'Novedades_Operador_DCS': 'DCS',
    };

    for (const role in areaMap) {
      if (this.authService.hasRole(role)) {
        return `Informe Operador ${areaMap[role]}`;
      }
    }

    // Fallback
    return 'Informe de Turno';
  }

  /**
   * Obtiene el perfil del usuario
   */
  getUserProfile(): string {
    return this.authService.getUserProfile();
  }

  openConfirmPopup(): void {
    this.showConfirmPopup = true; // Mostrar el popup de confirmación
  }

  closeConfirmPopup(): void {
    this.showConfirmPopup = false; // Ocultar el popup de confirmación
  }

  confirmStartShift(): void {
    this.closeConfirmPopup(); // Cerrar el popup antes de iniciar el turno
    this.startShiftWithPrecheck(this.shiftInfo.hora);
  }

  /**
   * Inicia turno 'informe_turno' con una validación previa para evitar duplicados.
   * Reglas:
   * - Si existe un turno abierto (GET /shiftclosure/open/informe_turno/user/{id} devuelve ID), no iniciar y avisar.
   * - Si 404 (no hay turno), proceder a iniciar con POST /shiftclosure/start.
   */
  private startShiftWithPrecheck(hora: string): void {
    // Paso 1: Verificar turno_diario abierto (global)
    this.shiftClosureService.getOpenDailyShift().subscribe({
      next: (dailyId: number) => {
        if (!dailyId) {
          this.snackBar.open('No existe turno diario abierto. Intenta nuevamente más tarde.', 'Cerrar', { duration: 6000 });
          return;
        }
        // Paso 2: Verificar si ya existe turno informe_turno para el usuario
        this.shiftClosureService.getOpenShiftByType(Constants.INFORME_TURNO).subscribe({
          next: (shiftId: number) => {
            if (shiftId) {
              this.snackBar.open('Ya existe un turno abierto del tipo informe_turno para tu usuario.', 'Cerrar', { duration: 5000 });
              return;
            }
            this.postStartShift(hora);
          },
          error: (err2) => {
            if (err2?.status === 404) {
              // No hay turno informe_turno del usuario: podemos iniciar
              this.postStartShift(hora);
            } else {
              console.error('Error al verificar turno informe_turno:', err2);
              const msg2 = typeof err2?.error === 'string' ? err2.error : 'Error al verificar turno de usuario.';
              this.snackBar.open(msg2, 'Cerrar', { duration: 6000 });
            }
          }
        });
      },
      error: (err1) => {
        if (err1?.status === 404) {
          this.snackBar.open('No existe turno diario abierto. Intenta nuevamente más tarde.', 'Cerrar', { duration: 6000 });
        } else {
          console.error('Error al verificar turno diario:', err1);
          const msg1 = typeof err1?.error === 'string' ? err1.error : 'Error al verificar turno diario.';
          this.snackBar.open(msg1, 'Cerrar', { duration: 6000 });
        }
      }
    });
  }

  private postStartShift(hora: string): void {
    const payload = { shiftClosureType: Constants.INFORME_TURNO, time: hora };
    this.shiftClosureService.startShift(payload).subscribe({
      next: () => {
        this.snackBar.open('Turno iniciado con éxito.', 'Cerrar', { duration: 3000 });
        this.modalInfo = true;
      },
      error: (error) => {
        console.error('Error al iniciar el turno:', error);
        const msg = typeof error?.error === 'string' ? error.error : 'Error al iniciar el turno.';
        this.snackBar.open(msg, 'Cerrar', { duration: 6000 });
      }
    });
  }

  logout() {
    if (!isPlatformBrowser(this.platformId)) return;

    try { this.oidc.logoffLocal(); } catch { }
    try { sessionStorage.clear(); } catch { }
    try { localStorage.removeItem('currentUser'); } catch { }

    const domain = environment.oidc.cognitoDomain;
    const url =
      `https://${domain}/logout` +
      `?client_id=${encodeURIComponent(environment.oidc.clientId)}` +
      `&logout_uri=${encodeURIComponent(environment.oidc.postLogoutRedirectUri)}`;

    window.location.href = url;
  }
}
